import React, { MouseEvent, useEffect, useMemo, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';

import {
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  Language as LanguageIcon,
  ManageSearch as ManageSearchIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Search as SearchIcon,
  Tag as TagIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  ChipProps,
  GridLegacy as Grid,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPagination, GridPaginationModel, MuiEvent } from '@mui/x-data-grid';

import { COUNTRIES } from '~/consts/priceParrot';

import useAppNavigation from '~/hooks/useAppNavigation';

import PriceParrotService from '~/services/PriceParrotService';
import { formatDateSlashesWithClock } from '~/services/TimeService';

import {
  PaginatedPriceParrotQueryParams,
  ScheduleInterval,
  ScheduledPriceSummaryJob,
  ScheduledPriceSummaryJobStatus,
} from '~/types/services/priceParrot';

import getAllCurrencyCodes from '~/utils/getAllCurrencyCodes';
import { errorToString, extractUsernameFromEmail } from '~/utils/stringUtils';

import LocationAutocomplete from './LocationAutocomplete';

type FormInput = {
  locationUrl: string;
  scheduleInterval: ScheduleInterval;
  currencyCode: string;
  countryCode: string;
};

type SearchFormInput = {
  query: string;
  status: ScheduledPriceSummaryJobStatus | '';
  tags: string;
};

type PageState = {
  loading: boolean;
  error: Error | undefined;
  gridPaginationModel: GridPaginationModel;
  rowCount: number;
  searchFormSubmission: SearchFormInput;
};

export default function PriceParrotScheduler() {
  const [pageState, setPageState] = useState<PageState>({
    loading: false,
    error: undefined,
    gridPaginationModel: { pageSize: 100, page: 0 },
    rowCount: 0,
    searchFormSubmission: {
      query: '',
      status: '',
      tags: '',
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting, isValid },
  } = useForm<FormInput>({
    defaultValues: {
      locationUrl: '',
      scheduleInterval: ScheduleInterval.WEEKLY,
      currencyCode: 'AUD',
      countryCode: 'AU',
    },
  });
  const searchForm = useForm<SearchFormInput>({
    defaultValues: {
      query: '',
      status: '',
      tags: '',
    },
  });
  const { push } = useAppNavigation();
  const [scheduledJobData, setScheduledJobData] = useState<Array<ScheduledPriceSummaryJob>>([]);
  const jobQueryParams = useMemo<PaginatedPriceParrotQueryParams>(
    () => ({
      page: pageState.gridPaginationModel.page + 1,
      limit: pageState.gridPaginationModel.pageSize,
      query: pageState.searchFormSubmission.query,
      status: pageState.searchFormSubmission.status !== '' ? pageState.searchFormSubmission.status : undefined,
      tags: pageState.searchFormSubmission.tags,
    }),
    [pageState.gridPaginationModel, pageState.searchFormSubmission],
  );
  const columns: Array<GridColDef<ScheduledPriceSummaryJob>> = [
    {
      field: 'property',
      headerName: 'Property',
      flex: 4,
      display: 'flex',
      renderCell: ({ row: { property } }) => (
        <Link
          variant="body1"
          href={property.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {property.name ?? property.url}
        </Link>
      ),
    },
    { field: 'scheduleInterval', headerName: 'Schedule Interval', flex: 2, display: 'flex' },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      display: 'flex',
      renderCell: ({ row: { status } }) => {
        let color: ChipProps['color'] = 'default';
        switch (status) {
          case ScheduledPriceSummaryJobStatus.ACTIVE:
            color = 'success';
            break;
          case ScheduledPriceSummaryJobStatus.PAUSED:
            color = 'warning';
            break;
        }

        return (
          <Stack direction="row">
            <Chip
              size="small"
              color={color}
              label={status.toUpperCase()}
              style={{ transition: 'background-color 0.5s ease' }}
            />
          </Stack>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      flex: 2,
      display: 'flex',
      renderCell: ({ row: { createdAt, createdBy, createdByEmail } }) => (
        <Box>
          <Typography component="div">{formatDateSlashesWithClock(createdAt)}</Typography>
          {createdBy && createdByEmail && (
            <Typography component="div" sx={{ wordBreak: 'break-word' }}>
              by{' '}
              <Link href={`/users/${createdBy}`} onClick={(e) => e.stopPropagation()}>
                {extractUsernameFromEmail(createdByEmail)}
              </Link>
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'nextRunAt',
      headerName: 'Next Run At',
      flex: 2,
      display: 'flex',
      valueFormatter: formatDateSlashesWithClock,
    },
    {
      field: 'variations',
      headerName: 'Variations',
      flex: 1,
      display: 'flex',
      valueGetter: (value, row) => row.variations.length,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 2,
      display: 'flex',
      renderCell: ({ row }) => (
        <Stack direction="row">
          {row.status === ScheduledPriceSummaryJobStatus.ACTIVE && (
            <Tooltip title="Pause Schedule">
              <IconButton
                onClick={async (event) =>
                  await handleUpdateJobStatus(event, row, ScheduledPriceSummaryJobStatus.PAUSED)
                }
                sx={{ padding: '4px' }}
              >
                <PauseIcon />
              </IconButton>
            </Tooltip>
          )}
          {row.status === ScheduledPriceSummaryJobStatus.PAUSED && (
            <Tooltip title="Start Schedule">
              <IconButton
                onClick={async (event) =>
                  await handleUpdateJobStatus(event, row, ScheduledPriceSummaryJobStatus.ACTIVE)
                }
                sx={{ padding: '4px' }}
              >
                <PlayArrowIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  const handleSubmitJob = async (data: FormInput): Promise<void> => {
    try {
      const { job } = await PriceParrotService.postScheduledPriceSummaryJob({
        ...data,
        scheduledJobVariations: [
          {
            currencyCode: data.currencyCode,
            countryCode: data.countryCode,
            numberOfNights: 3,
            offsetDays: 1,
            totalDays: 365,
          },
        ],
      });
      // If the user is on the first page, add the new job to the top of the list
      pageState.gridPaginationModel.page === 0 && setScheduledJobData((prev) => [job, ...prev]);
      reset();
    } catch (e) {
      setPageState((prev) => ({ ...prev, error: e }));
      console.error(e);
    }
  };

  const handleUpdateJobStatus = async (
    event: MuiEvent<MouseEvent>,
    job: ScheduledPriceSummaryJob,
    newStatus: ScheduledPriceSummaryJobStatus,
  ): Promise<void> => {
    // prevents the row onclick from firing
    event.stopPropagation();

    try {
      const { id, scheduleInterval } = job;
      await PriceParrotService.patchScheduledPriceSummaryJob(id, {
        scheduleInterval,
        status: newStatus,
      });
      setScheduledJobData((prev) => prev.map((job) => (job.id === id ? { ...job, status: newStatus } : job)));
    } catch (e) {
      setPageState((prev) => ({ ...prev, error: e }));
      console.error(e);
    }
  };

  const submitScheduleSearch = async (data: SearchFormInput): Promise<void> => {
    setPageState((prev) => ({ ...prev, searchFormSubmission: data }));
  };

  useEffect(() => {
    setPageState((prev) => ({ ...prev, loading: true }));
    PriceParrotService.getScheduledPriceSummaryJobs(jobQueryParams)
      .then(({ data, pagination }) => {
        setScheduledJobData(data);
        setPageState((prev) => ({ ...prev, rowCount: pagination.total }));
      })
      .catch((e) => setPageState((prev) => ({ ...prev, error: e })))
      .finally(() => setPageState((prev) => ({ ...prev, loading: false })));
  }, [jobQueryParams]);

  return (
    <Box>
      <Stack direction="column" gap={2}>
        {pageState.error && (
          <Alert severity="error" onClose={() => setPageState((prev) => ({ ...prev, error: undefined }))}>
            {errorToString(pageState.error)}
          </Alert>
        )}

        <Typography variant="h6">Search for an existing schedule</Typography>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} sm={4}>
            <Controller
              name="query"
              control={searchForm.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Search Query"
                  disabled={isSubmitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ManageSearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="status"
              control={searchForm.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Status"
                  disabled={isSubmitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTimeIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                >
                  <MenuItem key="all" value="">
                    ALL
                  </MenuItem>
                  {Object.values(ScheduledPriceSummaryJobStatus).map((status: ScheduledPriceSummaryJobStatus) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="tags"
              control={searchForm.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Tag"
                  disabled={isSubmitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TagIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="end" gap={1}>
          <Button
            variant="contained"
            type="submit"
            disabled={searchForm.formState.isSubmitting}
            startIcon={<SearchIcon />}
            onClick={searchForm.handleSubmit(submitScheduleSearch)}
          >
            Search Schedule
          </Button>
        </Stack>

        <Typography variant="h6">Submit a new schedule</Typography>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} sm={6}>
            <LocationAutocomplete
              name="locationUrl"
              control={control}
              rules={{ required: 'Location Link is required' }}
              disabled={isSubmitting}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Controller
              name="scheduleInterval"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Schedule Interval"
                  disabled={isSubmitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTimeIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                >
                  {Object.values(ScheduleInterval)
                    .filter((i: ScheduleInterval) => i !== ScheduleInterval.ADHOC)
                    .map((interval: ScheduleInterval) => (
                      <MenuItem key={interval} value={interval}>
                        {interval}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Controller
              name="currencyCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => {
                    // i.e. AUD -> AU
                    const matchedCountry: string | undefined = COUNTRIES.find(
                      (c) => c.code === e.target.value.substring(0, 2),
                    )?.code;
                    matchedCountry && setValue('countryCode', matchedCountry);
                    field.onChange(e.target.value);
                  }}
                  select
                  fullWidth
                  label="Currency"
                  disabled={isSubmitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                >
                  {getAllCurrencyCodes().map((currencyCode: string) => (
                    <MenuItem key={currencyCode} value={currencyCode}>
                      {currencyCode}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Controller
              name="countryCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Country"
                  disabled={isSubmitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LanguageIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                >
                  {COUNTRIES.map(({ name, code }) => (
                    <MenuItem key={code} value={code}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="end" gap={1}>
          <Button
            variant="contained"
            type="submit"
            disabled={!isValid || isSubmitting}
            onClick={handleSubmit(handleSubmitJob)}
          >
            Submit Schedule
          </Button>
        </Stack>
      </Stack>

      <DataGrid
        autoHeight
        columns={columns}
        rows={scheduledJobData}
        loading={pageState.loading}
        getRowId={(row) => row.id}
        getRowHeight={() => 'auto'}
        /* Server Side Pagination */
        rowCount={pageState.rowCount}
        pagination
        paginationMode="server"
        paginationModel={pageState.gridPaginationModel}
        onPaginationModelChange={(model) => setPageState((prev) => ({ ...prev, gridPaginationModel: model }))}
        slots={{
          pagination: GridPagination,
        }}
        disableRowSelectionOnClick
        disableColumnSorting
        disableColumnMenu
        onRowClick={(params: { row: ScheduledPriceSummaryJob }) =>
          push(`/commercial-ops/price-parity/scheduler/jobs/${params.row.id}`)
        }
      />
    </Box>
  );
}
